import { parseQyooId } from "../../src/domain/qyoo-id";
import catalog from "./data/designer-catalog.json";

export type PatternTransform = "rotate" | "horizontal" | "vertical";
export interface PatternVariant { id: number; labels: string[] }
export interface CatalogEntry { decimal: number; binary: string; classification: string; names: string[] }
export const designerCatalog = catalog;

export function patternBits(id: number | string): string {
  return parseQyooId(id).toString(2).padStart(36, "0");
}

export function patternID(bits: string): number {
  if (!/^[01]{36}$/.test(bits)) throw new Error("Enter exactly 36 binary digits.");
  return Number.parseInt(bits, 2);
}

/** Transform only the payload grid; the oriented carrier remains canonical. */
export function transformPattern(id: number, transform: PatternTransform): number {
  const input = patternBits(id);
  const output = Array<string>(36);
  for (let row = 0; row < 6; row++) {
    for (let column = 0; column < 6; column++) {
      const [nextRow, nextColumn] = transform === "rotate" ? [column, 5 - row]
        : transform === "horizontal" ? [row, 5 - column] : [5 - row, column];
      output[nextRow * 6 + nextColumn] = input[row * 6 + column]!;
    }
  }
  return patternID(output.join(""));
}

export function patternFamily(id: number): PatternVariant[] {
  const variants = new Map<number, PatternVariant>();
  for (const mirrored of [false, true]) {
    let current = mirrored ? transformPattern(id, "horizontal") : parseQyooId(id);
    for (let turns = 0; turns < 4; turns++) {
      const label = mirrored ? `Mirror + ${turns * 90}°` : turns ? `${turns * 90}°` : "Original";
      const entry = variants.get(current) ?? { id: current, labels: [] };
      entry.labels.push(label);
      variants.set(current, entry);
      current = transformPattern(current, "rotate");
    }
  }
  return [...variants.values()];
}

export function classifyPattern(id: number, entries: readonly CatalogEntry[] = catalog.entries) {
  parseQyooId(id);
  const exact = entries.find(entry => entry.decimal === id);
  const familyIDs = new Set(patternFamily(id).map(entry => entry.id));
  const related = entries.filter(entry => entry.decimal !== id && familyIDs.has(entry.decimal));
  const classification = exact?.classification === "historicalSample" ? "system" : exact?.classification ?? "ordinary";
  return { classification, exact, related };
}
